import {isFeatureEnabled} from '@github-ui/feature-flags'
import {sendEvent} from '@github-ui/hydro-analytics'
import {ssrSafeLocation} from '@github-ui/ssr-utils'
import {ReplyIcon} from '@primer/octicons-react'
import type {SelectPanelProps} from '@primer/react'
import {SelectPanel} from '@primer/react'
import type {KeyboardEventHandler, RefObject} from 'react'
import {createContext, useCallback, useContext, useEffect, useImperativeHandle, useState} from 'react'

import styles from './SavedReplies.module.css'
import {ToolbarButton} from './ToolbarButton'

export type SavedReply = {
  name: string
  content: string
}

export type SavedRepliesHandle = {
  openMenu: () => void
}

type SavedRepliesContext = null | {
  onSelect: (savedReply: SavedReply) => void
  onOpen: () => void
  savedReplies: SavedReply[]
  /** Ref to the button for clicking via keyboard shortcut. */
  ref: RefObject<SavedRepliesHandle | null>
}

type Item = SelectPanelProps['items'][number]

// SavedRepliesContext is separate from MarkdownEditorContext because the saved replies array is practically guarunteed to change
// on every render. If it was provided in the MarkdownEditorContext, it would cause the whole editor to rerender on every render.
export const SavedRepliesContext = createContext<SavedRepliesContext>(null)

export const SavedRepliesButton = () => {
  const context = useContext(SavedRepliesContext)
  const issuesReactLazyLoad = isFeatureEnabled('issues_lazy_load_comment_box_suggestions')

  useImperativeHandle(context?.ref, () => ({
    openMenu: () => {
      // eslint-disable-next-line react-hooks/immutability
      setOpen(true)
    },
  }))

  const [open, setOpen] = useState(false)
  useEffect(() => {
    if (!issuesReactLazyLoad) {
      // eslint-disable-next-line react-hooks/immutability
      setFilter('')

      if (open) {
        sendEvent('markdown_editor.open_saved_replies')
        context?.onOpen()
      }
    }
  }, [context, issuesReactLazyLoad, open])

  const [filter, setFilter] = useState('')

  // there's not much point in memoizing this since the savedReplies array is likely to change on every render
  const items = context?.savedReplies
    .filter(({name}) => name.toLowerCase().includes(filter.toLowerCase()))
    .map(
      (reply, i): Item => ({
        id: i,
        text: reply.name,
        description: reply.content,
        descriptionVariant: 'block',
        trailingVisual: i < 9 ? `Ctrl + ${i + 1}` : undefined,
        className: styles.SavedReplies,
      }),
    )

  const onSelectItem = (item: Item | undefined) => {
    setOpen(false)
    const reply = context?.savedReplies.find(({name}) => name === item?.text)
    if (reply) context?.onSelect(reply)
  }

  const onKeyDown: KeyboardEventHandler = event => {
    // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
    const keyInt = parseInt(event.key, 10)
    // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
    if (items && event.ctrlKey && !Number.isNaN(keyInt) && keyInt >= 1 && keyInt <= 9) {
      event.stopPropagation()
      event.preventDefault()
      onSelectItem(items[keyInt - 1])
    }
  }

  const onOpenChangeHandler = useCallback(
    (openState: boolean) => {
      setOpen(openState)

      if (!issuesReactLazyLoad) return

      if (!openState) {
        setFilter('')
      } else {
        sendEvent('markdown_editor.open_saved_replies')
        context?.onOpen()
      }
    },
    [context, issuesReactLazyLoad],
  )

  return items ? (
    <SelectPanel
      renderAnchor={props => (
        <ToolbarButton {...props} icon={ReplyIcon} aria-label="Saved replies" aria-labelledby={undefined} />
      )}
      open={open}
      onOpenChange={onOpenChangeHandler}
      items={items}
      filterValue={filter}
      onFilterChange={setFilter}
      placeholderText="Search saved replies"
      selected={undefined}
      onSelectedChange={(selection: Item | Item[] | undefined) => {
        onSelectItem(Array.isArray(selection) ? selection[0] : selection)
      }}
      overlayProps={{width: 'small', maxHeight: 'small', anchorSide: 'outside-right', onKeyDown}}
      secondaryAction={
        <SelectPanel.SecondaryActionLink href={getSavedRepliesLink()}>Create new</SelectPanel.SecondaryActionLink>
      }
    />
  ) : (
    <></>
  )
}

function getSavedRepliesLink() {
  return `${ssrSafeLocation.origin}/settings/replies`
}

try{ SavedRepliesContext.displayName ||= 'SavedRepliesContext' } catch {}
try{ SavedRepliesButton.displayName ||= 'SavedRepliesButton' } catch {}